import {UserManager, User} from 'oidc-client-ts';
import {PREVIOUS_PATH_KEY} from '@/constants/storage';
import environment from '@/environment';

export class AuthenticationService {
  private userManager: UserManager;

  private constructor(userManager?: UserManager) {
    this.userManager =
      userManager ||
      new UserManager({
        authority: environment.OAUTH_ISSUER,
        client_id: environment.OAUTH_CLIENT_ID,
        redirect_uri: `${window.location.origin}`,
        post_logout_redirect_uri: environment.LOGOUT_REDIRECT_URL,
        response_type: environment.OAUTH_RESPONSE_TYPE,
        scope: environment.OAUTH_SCOPE,
      });
  }

  static instance: AuthenticationService;

  static getInstance(userManager?: UserManager) {
    if (!this.instance) {
      this.instance = new AuthenticationService(userManager);
    } else if (userManager) {
      this.instance.userManager = userManager;
    }
    return this.instance;
  }

  async login() {
    await this.userManager.signinRedirect();
  }

  async clearStorage() {
    sessionStorage.removeItem(PREVIOUS_PATH_KEY);
    await this.userManager.removeUser();
  }

  logout = async () => {
    const user = await this.getCurrentUser();
    await this.clearStorage();

    if (user?.id_token) {
      await this.userManager.signoutRedirect({
        id_token_hint: user.id_token,
      });
    } else {
      await this.userManager.signoutRedirect();
    }
  };

  async handleCallback(url: string) {
    return await this.userManager.signinCallback(url);
  }

  async getCurrentUser(): Promise<User | null> {
    return this.userManager.getUser();
  }

  async getAccessToken(): Promise<string | undefined> {
    const user = await this.getCurrentUser();
    if (!user) {
      await this.login();
      return this.getAccessToken();
    }

    return user.access_token;
  }
}

export default AuthenticationService.getInstance();
